'use client';

import { ChangeEvent, FormEvent } from 'react';

import { Dayjs } from 'dayjs';

import Radio from '@/components/elements/radio';
import { useTranslation } from '@/i18n/client';
import { EventCardT } from '@/types/calendar';
import { EventSearchArgs } from '@/types/calendar/events';
import { WithLang } from '@/types/general';

interface Props {
    selectedDays: Dayjs[];
    events: EventCardT[];
    toggleSearchRangeOff: () => void;
    toggleSearchRangeOn: () => void;
    onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onSearchClear: () => void;
    sortByScore: () => void;
    sortByDate: () => void;
    onSearchSubmit: (e: FormEvent) => void;
    searchArgs: EventSearchArgs;
}

export default function CalendarSearch(props: WithLang<Props>) {
    const { t: tCa } = useTranslation(props.lang, 'calendar');
    const { t: tCo } = useTranslation(props.lang, 'common');

    return (
        <div className="flex flex-col overflow-hidden">
            <form className="w-full" onSubmit={props.onSearchSubmit}>
                <div className="flex w-full flex-col gap-x-4 gap-y-2 sm:flex-row">
                    <div className="w-full">
                        <label htmlFor="search">{tCa('controls.search')}</label>
                        <div className="relative">
                            <input
                                aria-label="Search"
                                id="search"
                                className="input-primary w-full pr-12"
                                onChange={props.onSearchChange}
                                value={props.searchArgs.searchString}
                            />
                            {props.searchArgs.searchString && (
                                <span
                                    onClick={props.onSearchClear}
                                    className="icon absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 cursor-pointer bg-phiX"
                                />
                            )}
                        </div>
                    </div>
                    <button
                        disabled={!props.searchArgs.searchString}
                        onSubmit={props.onSearchSubmit}
                        className="btn-accent mt-auto h-10 w-full sm:w-1/4"
                    >
                        {tCa('controls.toSearch')}
                    </button>
                </div>
            </form>
            <div className="flex gap-x-4">
                <div
                    className={`mt-4 flex flex-col gap-y-1 transition-all duration-200 ease-out ${props.searchArgs.searchString ? 'max-h-40' : 'max-h-0'}`}
                >
                    <span className="text-xs">{tCo('common.dateRange')}</span>
                    <Radio
                        checked={props.searchArgs.ranged}
                        onChange={props.toggleSearchRangeOn}
                        id="date-range-on"
                        label={tCa('controls.searchRanged')}
                    />
                    <Radio
                        checked={!props.searchArgs.ranged}
                        onChange={props.toggleSearchRangeOff}
                        id="date-range-off"
                        label={tCa('controls.searchNoRange')}
                    />
                </div>
                <div
                    className={`mt-4 flex flex-col gap-y-1 transition-all duration-200 ease-out ${props.searchArgs.searchString ? 'max-h-40' : 'max-h-0'}`}
                >
                    <span className="text-xs">{tCo('common.sorting')}</span>
                    <Radio
                        id="sorting-score"
                        checked={props.searchArgs.sortByScore}
                        onChange={props.sortByScore}
                        label={tCa('controls.sortByScore')}
                    />
                    <Radio
                        checked={!props.searchArgs.sortByScore}
                        onChange={props.sortByDate}
                        id="sorting-start-date"
                        label={tCa('controls.sortByDate')}
                    />
                </div>
            </div>
        </div>
    );
}
